import React, { useState, useEffect } from 'react';
import { addBrand, getBrands, deleteBrand } from '../firebase/services';
import BilingualInput from '../Components/BilingualInput';
import toast from 'react-hot-toast';
import './Admin.css';

export default function ManageBrands() {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    nameEn: '',
    nameAr: ''
  });

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    const brandsList = await getBrands();
    setBrands(brandsList);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) return toast.error("Please select an image");
    const combinedName = [formData.nameEn, formData.nameAr].filter(Boolean).join(' / ');
    if (!combinedName) return toast.error("Brand name (EN or AR) is required");

    setLoading(true);
    try {
      const brandData = {
        nameEn: formData.nameEn,
        nameAr: formData.nameAr,
        name: combinedName
      };
      await addBrand(imageFile, brandData);
      toast.success("Brand added successfully!");
      setImageFile(null);
      setImagePreview(null);
      setFormData({ nameEn: '', nameAr: '' });
      loadBrands();
    } catch (error) {
      toast.error("Error adding brand: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this brand?")) {
      try {
        await deleteBrand(id);
        toast.success("Brand deleted successfully!");
        loadBrands();
      } catch (error) {
        toast.error("Error deleting brand: " + error.message);
      }
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Manage Brands</h2>
        <p>Add popular brands to display on the home page</p>
      </div>

      <div className="form-grid">
        {/* Left Column: Form */}
        <div>
          <div className="admin-card">
            <div className="card-header">
              <h3>Add New Brand</h3>
            </div>
            <form onSubmit={handleSubmit}>
              <BilingualInput
                label="Brand Name"
                fieldNameEn="nameEn"
                fieldNameAr="nameAr"
                valueEn={formData.nameEn}
                valueAr={formData.nameAr}
                onChange={handleChange}
                placeholder="Enter brand name"
                required
              />

              <div className="input-group">
                <label>Brand Logo</label>
                <label htmlFor="brand-upload" className="image-upload-area">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <>
                      <div className="upload-icon">
                        <ion-icon name="cloud-upload-outline"></ion-icon>
                      </div>
                      <p>Click to upload brand logo</p>
                      <span className="helper-text">Supports: JPG, PNG, WEBP</span>
                    </>
                  )}
                </label>
                <input 
                  id="brand-upload"
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  style={{display: 'none'}}
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading} style={{width: '100%'}}>
                {loading ? 'Uploading...' : 'Add Brand'}
              </button>
            </form>
          </div>
        </div>

        {/* Right Column: List */}
        <div>
          <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
            <div className="card-header">
              <h3>Existing Brands</h3>
            </div>
            
            <div className="table-container" style={{border: 'none'}}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{width: '80px'}}>Logo</th>
                    <th>Name</th>
                    <th style={{textAlign: 'right'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map(brand => (
                    <tr key={brand.id}>
                      <td>
                        <img 
                          src={brand.imageUrl} 
                          alt={brand.name} 
                          style={{width: '50px', height: '50px', objectFit: 'contain', padding: '5px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#fff'}} 
                        />
                      </td>
                      <td>
                        <span style={{fontWeight: 500, color: '#111827'}}>{brand.name}</span>
                      </td>
                      <td style={{textAlign: 'right'}}>
                        <button 
                          className="action-btn delete"
                          onClick={() => handleDelete(brand.id)}
                          title="Delete Brand"
                        >
                          <ion-icon name="trash-outline"></ion-icon>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {brands.length === 0 && (
                    <tr>
                      <td colSpan="3" style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
                        No brands found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
